/**
 * InterLogs Trade — Independent Card Windows Manager for Right Feed
 * Controls multi-window expansion, minimize, wide, and close states right inside the feed.
 */

import { NewsItem, WindowCardState, ActiveCardWindow, SentimentType } from './types';
import { renderSentimentGauge } from './sentiment_gauge';

export class NewsWindowManager {
  private activeWindows: Map<string, ActiveCardWindow> = new Map();
  private container: HTMLElement;
  private headerControlContainer: HTMLElement | null = null;

  constructor(containerId: string = 'news-feed-container') {
    const el = document.getElementById(containerId);
    if (!el) {
      throw new Error(`Container #${containerId} not found in DOM`);
    }
    this.container = el;
    this.setupFeedHeaderControls();
  }

  private setupFeedHeaderControls(): void {
    const wireHeader = document.querySelector('.wire-header');
    if (!wireHeader) return;

    // Add window management toolbar if not already present
    let toolBar = document.getElementById('wire-windows-toolbar');
    if (!toolBar) {
      toolBar = document.createElement('div');
      toolBar.id = 'wire-windows-toolbar';
      toolBar.className = 'wire-windows-toolbar';
      toolBar.innerHTML = `
        <span class="win-toolbar-status" id="win-toolbar-status">Feed Stream Active</span>
        <div class="win-toolbar-actions" id="win-toolbar-actions" style="display: none;">
          <button class="win-tool-btn" id="btn-minimize-all" title="Minimize all open windows">_ Min All</button>
          <button class="win-tool-btn" id="btn-close-all" title="Close all open windows">✕ Close All</button>
        </div>
      `;
      wireHeader.appendChild(toolBar);

      toolBar.querySelector('#btn-minimize-all')?.addEventListener('click', () => {
        this.minimizeAllWindows();
      });

      toolBar.querySelector('#btn-close-all')?.addEventListener('click', () => {
        this.closeAllWindows();
      });
    }
    this.headerControlContainer = toolBar;
  }

  public updateWindowToolbar(): void {
    if (!this.headerControlContainer) return;
    const actions = this.headerControlContainer.querySelector('#win-toolbar-actions') as HTMLElement;
    const status = this.headerControlContainer.querySelector('#win-toolbar-status') as HTMLElement;
    const openCount = Array.from(this.activeWindows.values()).filter(w => w.state === 'window_open').length;

    if (actions && status) {
      if (openCount > 0) {
        actions.style.display = 'flex';
        status.textContent = `${openCount} Independent Window${openCount > 1 ? 's' : ''} Open`;
        status.className = 'win-toolbar-status active';
      } else {
        actions.style.display = 'none';
        status.textContent = 'Feed Stream Active';
        status.className = 'win-toolbar-status';
      }
    }
  }

  public createCardElement(item: NewsItem, isNew: boolean = false): HTMLElement {
    const card = document.createElement('article');
    card.id = `card-${item.id}`;
    card.dataset.id = item.id;
    card.dataset.category = item.category || 'macro';
    card.dataset.sentiment = item.sentiment || 'neutral';

    // Render in default preview card state
    this.renderPreviewMode(card, item, isNew);

    return card;
  }

  /**
   * Renders a card in its standard, compact preview feed format.
   */
  public renderPreviewMode(card: HTMLElement, item: NewsItem, isNew: boolean = false): void {
    card.className = 'wire-card' + (isNew ? ' new-alert' : '');
    card.classList.remove('wire-window-card', 'window-open', 'window-minimized', 'window-wide');

    const sourceClass = (item.source || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const sourceLabel = item.source || 'WIRE';
    const timeStr = this.formatRelativeTime(item.published_at);

    let sentimentLabel = '● NEUTRAL';
    if (item.sentiment === 'positive') sentimentLabel = '● BULLISH';
    else if (item.sentiment === 'negative') sentimentLabel = '● BEARISH';

    const tickerChipsHtml = (item.tickers || [])
      .map(t => `<span class="wire-ticker-chip">${this.escapeHtml(t)}</span>`)
      .join('');

    const previewText = item.summary && item.summary.trim().length > 0
      ? item.summary
      : item.title;

    card.innerHTML = `
      <div class="wire-card-header">
        <div class="wire-card-meta-left">
          <span class="wire-source-tag ${sourceClass}">${this.escapeHtml(sourceLabel)}</span>
          <span class="wire-time">${timeStr}</span>
        </div>
        <div class="wire-card-meta-right">
          <span class="sentiment-badge ${item.sentiment}">${sentimentLabel}</span>
          <button class="btn-card-open-window" title="Open as Independent Window in Feed">⤢ Open Window</button>
        </div>
      </div>

      <h3 class="wire-card-title">${this.escapeHtml(item.title)}</h3>

      <p class="wire-summary">${this.escapeHtml(previewText)}</p>

      <div class="wire-card-footer">
        <div class="wire-tickers-list">${tickerChipsHtml}</div>
        <div class="wire-card-actions">
          <button class="wire-btn-action wire-btn-open-win" title="Open as independent window in feed">
            <span>Open Window</span>
            <span>⤢</span>
          </button>
          <a href="${this.escapeHtml(item.url)}" target="_blank" rel="noopener noreferrer" class="wire-btn-action wire-btn-source" title="External publisher link" onclick="event.stopPropagation()">
            <span>Source</span>
            <span>↗</span>
          </a>
        </div>
      </div>
    `;

    // Click handler to open as independent window
    card.onclick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('.wire-btn-source')) {
        return; // Allow external navigation
      }
      this.openWindow(item.id, item, card);
    };

    // If card was in activeWindows, clean up or set to preview
    this.activeWindows.delete(item.id);
    this.updateWindowToolbar();
  }

  /**
   * Opens the card as a full-fledged Independent Window Card inside the right feed.
   */
  public openWindow(id: string, item: NewsItem, card: HTMLElement): void {
    card.className = 'wire-window-card window-open';
    card.onclick = null; // Clear card-level click

    const sourceClass = (item.source || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const sourceLabel = item.source || 'WIRE';
    const timeStr = this.formatRelativeTime(item.published_at);
    const exactDateStr = item.published_at
      ? new Date(item.published_at * 1000).toUTCString()
      : 'Live Wire Timestamp';

    let sentimentLabel = '● NEUTRAL';
    if (item.sentiment === 'positive') sentimentLabel = '● BULLISH';
    else if (item.sentiment === 'negative') sentimentLabel = '● BEARISH';

    const tickerChipsHtml = (item.tickers || [])
      .map(t => `<span class="wire-ticker-chip window-chip">${this.escapeHtml(t)}</span>`)
      .join('');

    const gaugeHtml = renderSentimentGauge(item.sentiment_score, item.sentiment);
    const analysisHtml = this.buildStructuredAnalysisHtml(item);

    card.innerHTML = `
      <!-- Independent Window Titlebar -->
      <div class="window-titlebar">
        <div class="window-titlebar-left">
          <span class="window-traffic-dot ${item.sentiment}" title="Sentiment status: ${sentimentLabel}"></span>
          <span class="wire-source-tag ${sourceClass}">${this.escapeHtml(sourceLabel)}</span>
          <span class="window-category-pill">${this.escapeHtml((item.category || 'macro').toUpperCase())}</span>
          <span class="window-time-meta">${timeStr}</span>
        </div>
        <div class="window-controls">
          <button class="window-control-btn btn-win-minimize" title="Minimize window to titlebar" aria-label="Minimize window">_</button>
          <button class="window-control-btn btn-win-wide" title="Toggle wide window mode" aria-label="Toggle wide window">⤢</button>
          <button class="window-control-btn btn-win-close" title="Close window back to feed preview" aria-label="Close window">✕</button>
        </div>
      </div>

      <!-- Window Interior -->
      <div class="window-interior">
        <div class="window-headline-row">
          <h2 class="window-title">${this.escapeHtml(item.title)}</h2>
          <span class="sentiment-badge ${item.sentiment}">${sentimentLabel}</span>
        </div>

        <div class="window-exact-time">${this.escapeHtml(exactDateStr)}</div>

        <!-- Loughran-McDonald Sentiment Gauge -->
        ${gaugeHtml}

        <!-- Structured Institutional Analysis -->
        <div class="window-analysis-container">
          ${analysisHtml}
        </div>

        ${tickerChipsHtml ? `
          <div class="window-tickers-row">
            <span class="window-tickers-lbl">IMPACTED MARKET ASSETS:</span>
            <div class="wire-tickers-list">${tickerChipsHtml}</div>
          </div>
        ` : ''}

        <!-- Window Action Toolbar -->
        <div class="window-action-bar">
          <a href="${this.escapeHtml(item.url)}" target="_blank" rel="noopener noreferrer" class="window-action-btn window-btn-source">
            <span>Open Original Story on ${this.escapeHtml(sourceLabel)}</span>
            <span>↗</span>
          </a>
          <button class="window-action-btn window-btn-copy" data-url="${this.escapeHtml(item.url)}">
            <span>Copy Story Link</span>
            <span>📋</span>
          </button>
          <button class="window-action-btn window-btn-dismiss">
            <span>Close Window ✕</span>
          </button>
        </div>
      </div>
    `;

    // Attach Window Control Listeners
    const btnMin = card.querySelector('.btn-win-minimize');
    const btnWide = card.querySelector('.btn-win-wide');
    const btnClose = card.querySelector('.btn-win-close');
    const btnDismiss = card.querySelector('.window-btn-dismiss');
    const btnCopy = card.querySelector('.window-btn-copy') as HTMLButtonElement;

    btnMin?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.minimizeWindow(id, item, card);
    });

    btnWide?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.toggleWideWindow(id, card);
    });

    btnClose?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.renderPreviewMode(card, item, false);
    });

    btnDismiss?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.renderPreviewMode(card, item, false);
    });

    if (btnCopy) {
      btnCopy.addEventListener('click', (e) => {
        e.stopPropagation();
        if (navigator.clipboard && item.url) {
          navigator.clipboard.writeText(item.url).then(() => {
            const original = btnCopy.innerHTML;
            btnCopy.innerHTML = `<span>Copied!</span><span>✓</span>`;
            setTimeout(() => { btnCopy.innerHTML = original; }, 1800);
          });
        }
      });
    }

    // Register active window
    this.activeWindows.set(id, {
      id,
      item,
      state: 'window_open',
      element: card,
      isWide: false
    });

    this.updateWindowToolbar();

    // Scroll card smoothly into view if needed
    card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  /**
   * Minimizes the window to a compact, single-line titlebar strip with a restore action.
   */
  public minimizeWindow(id: string, item: NewsItem, card: HTMLElement): void {
    card.className = 'wire-window-card window-minimized';
    card.onclick = null;

    const sourceClass = (item.source || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const sourceLabel = item.source || 'WIRE';

    card.innerHTML = `
      <div class="window-titlebar minimized-titlebar">
        <div class="window-titlebar-left">
          <span class="window-traffic-dot ${item.sentiment}"></span>
          <span class="wire-source-tag ${sourceClass}">${this.escapeHtml(sourceLabel)}</span>
          <span class="window-minimized-title" title="${this.escapeHtml(item.title)}">${this.escapeHtml(item.title)}</span>
        </div>
        <div class="window-controls">
          <button class="window-control-btn btn-win-restore" title="Restore full window" aria-label="Restore window">▲</button>
          <button class="window-control-btn btn-win-close" title="Close window" aria-label="Close window">✕</button>
        </div>
      </div>
    `;

    card.querySelector('.btn-win-restore')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.openWindow(id, item, card);
    });

    card.querySelector('.btn-win-close')?.addEventListener('click', (e) => {
      e.stopPropagation();
      this.renderPreviewMode(card, item, false);
    });

    // Clicking anywhere on minimized bar restores it
    card.onclick = () => {
      this.openWindow(id, item, card);
    };

    const winObj = this.activeWindows.get(id);
    if (winObj) {
      winObj.state = 'window_minimized';
    }

    this.updateWindowToolbar();
  }

  /**
   * Toggles widened / emphasized mode for the window card.
   */
  public toggleWideWindow(id: string, card: HTMLElement): void {
    const isWide = card.classList.toggle('window-wide');
    const winObj = this.activeWindows.get(id);
    if (winObj) {
      winObj.isWide = isWide;
    }
  }

  public minimizeAllWindows(): void {
    for (const [id, win] of this.activeWindows.entries()) {
      if (win.state === 'window_open') {
        this.minimizeWindow(id, win.item, win.element);
      }
    }
  }

  public closeAllWindows(): void {
    for (const [id, win] of this.activeWindows.entries()) {
      this.renderPreviewMode(win.element, win.item, false);
    }
    this.activeWindows.clear();
    this.updateWindowToolbar();
  }

  public hasOpenWindow(id: string): boolean {
    const win = this.activeWindows.get(id);
    return win !== undefined && win.state === 'window_open';
  }

  private buildStructuredAnalysisHtml(item: NewsItem): string {
    const cat = (item.category || 'macro').toLowerCase();
    const sent = item.sentiment || 'neutral';
    const tickers = item.tickers || [];
    const tickerNames = tickers.length > 0 ? tickers.join(', ') : 'Global Benchmarks';
    const summary = (item.summary || '').trim();

    let contextParagraph = '';
    if (cat === 'commodities') {
      contextParagraph = `Physical energy and commodities trading desks report immediate shifts in prompt supply differentials. Commercial stockpiles, tanker freight rates, and supply-chain logistics are actively pricing in revised balances across ${tickerNames}.`;
    } else if (cat === 'crypto') {
      contextParagraph = `On-chain liquidity metrics and institutional derivative order books reflect accelerated capital positioning following this update. Funding rates across perpetuals signal high market engagement in ${tickerNames}.`;
    } else {
      contextParagraph = `Macro portfolio managers and quantitative trading desks are re-evaluating risk premia across equity benchmarks and Treasury yields. Volatility indexes reflect heightened institutional positioning in ${tickerNames}.`;
    }

    let catalystOutlook = '';
    if (sent === 'positive') {
      catalystOutlook = `Bullish momentum indicators across ${tickerNames} are establishing higher support levels. Market participants are positioning for continued upside liquidity expansion and follow-through buying in upcoming sessions.`;
    } else if (sent === 'negative') {
      catalystOutlook = `Defensive hedging flows and risk-off rotation are prevalent across ${tickerNames}. Traders are monitoring key support bands and moving averages for signs of downside stabilization.`;
    } else {
      catalystOutlook = `Asset prices in ${tickerNames} remain in a consolidated equilibrium range as investors await secondary macroeconomic confirmations and upcoming central bank commentary.`;
    }

    return `
      <div class="window-section-block window-brief-block">
        <h4 class="window-section-title">EXECUTIVE BRIEF</h4>
        <p class="window-text-para">${this.escapeHtml(summary || item.title)}</p>
      </div>
      <div class="window-section-grid">
        <div class="window-section-block">
          <h4 class="window-section-title">MARKET CATALYST & CONTEXT</h4>
          <p class="window-text-para">${this.escapeHtml(contextParagraph)}</p>
        </div>
        <div class="window-section-block">
          <h4 class="window-section-title">PRICE ACTION & TRADING OUTLOOK</h4>
          <p class="window-text-para">${this.escapeHtml(catalystOutlook)}</p>
        </div>
      </div>
    `;
  }

  private formatRelativeTime(timestamp: number): string {
    if (!timestamp) return 'just now';
    const now = Math.floor(Date.now() / 1000);
    const diff = Math.max(0, now - timestamp);
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
  }

  private escapeHtml(str: string): string {
    if (!str) return '';
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
